// Dependency imports
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import NotStartedIcon from '@mui/icons-material/NotStarted';
import { useSelector } from 'react-redux';
import { IconButton } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import moment from 'moment';

// Local imports
import { useLiveInference } from 'hooks';
import { Repeat2 } from 'lucide-react';
import { Button, Modal } from 'components';
import {
  useGetAllActivities,
  useGetConfiguration,
  useGetWorkflowDetail,
  useListConfigurations,
  useListInferenceModels
} from 'api/sdk';
import { RootState } from 'store';
import { useListCameraDetails } from 'api/sdk';
import StreamErrorImage from '../../assets/images/error.svg';
import { Stop } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import SearchSelect from 'components/SearchSelect';
import Swal from 'sweetalert2';
import DependancyGraph from 'pages/configuration/AnalysisConfiguration/WorkflowMonitor/dependancyGraph';
import {
  setLiveState,
  setCameraId,
  setInferenceTriggered,
  setPreviousModel,
  setWorkflowId,
  setPreviousWorkflow
} from 'store/config/cameraLive/cameraSlice';
import StreamView from './liveinference/streamView';
import NotificationItem from './liveinference/notificationItems';

const LiveInference = () => {
  const {
    startLive,
    started,
    loading,
    setLoading,
    endInference,
    startStream,
    connectionStatus,
    resumeInference,
    restartInference,
    streamError,
    endStream,
    activityData,
    setModelData,
    setStreamError,
    image,
    modelData,
    setImage,
    modelLoaded,
    sendMessage,
    isReseting,
    setResetting,
    socket,
    setModelLoaded
  } = useLiveInference();
  const dispatch = useDispatch();
  const divRef = useRef<any>(null);

  const lastTriggerTimestamp = useSelector(
    (state: RootState) => state.cameraLive.lastTriggerTimestamp
  );

  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null
  );
  const [selectedWorkflowData, setSelectedWorkflowData] = useState<any>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [cameraId, setLocalCameraId] = useState<string | null>();

  //Selectors
  const previousModel = useSelector(
    (state: RootState) => state.cameraLive.previousModel
  );

  const previousWorkflow = useSelector(
    (state: RootState) => state.cameraLive.previousWorkflow
  );

  const profile = useSelector((state: RootState) => state.auth.profile);

  const siteId = useSelector(
    (data: any) => data?.auth?.profile?.session_site?.id
  );
  const currentConfigId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const cameraLiveState = useSelector(
    (state: RootState) => state.cameraLive.liveState
  );

  const { data: allActivities } = useGetAllActivities(currentConfigId || '', {
    pipeline_satus: true || null
  });
  console.log('socket connection status', connectionStatus);

  // API Hooks
  const { data: configs } = useListConfigurations();
  const { data: getConfigurationData } = useGetConfiguration({
    config_id: currentConfigId || ''
  });
  const { data: cameraData } = useListCameraDetails();
  const { data: workflowData, refetch: refetchWorkflow } = useGetWorkflowDetail(
    selectedModel || ''
  );

  const {
    data: models,
    refetch: refetchModels,
    isLoading: modelsLoading
  } = useListInferenceModels(
    siteId,
    {
      config_id: currentConfigId
    },
    {
      query: {
        enabled: !!currentConfigId
      }
    }
  );

  // Memoized Values
  const modelOptions = useMemo(
    () =>
      models?.data?.map(model => ({
        label: model.model_name,
        value: model.id
      })) || [],
    [models?.data]
  );

  const workflowOptions = useMemo(() => {
    console.log('workflowdata', workflowData);
    if (!workflowData?.data || !Array.isArray(workflowData.data)) return [];

    return workflowData.data.map(workflow => ({
      label: workflow.name,
      value: workflow.id,
      modelId: selectedModel
    }));
  }, [workflowData?.data, selectedModel, modelData]);

  const getModelData = useCallback(
    (modelName: string, workflowData?: any) => {
      const model = models?.data?.find(item => item.id === modelName);

      return {
        configuration_id: currentConfigId,
        org_id: profile?.organization?.id,
        model_ckpt: model?.model_path,
        model_config: model?.model_cfg,
        model_type: model?.model_type,
        model_id: model?.id,
        class_map: model?.class_map,
        workflow_steps: workflowData,
        model_created_at: model?.created_at
      };
    },
    [
      models,
      currentConfigId,
      profile,
      selectedWorkflowData,
      selectedWorkflowId,
      workflowData
    ]
  );

  const handleModelChange = useCallback(
    (value: string) => {
      setModelLoaded(false);
      setSelectedWorkflowId(null);
      setSelectedWorkflowData(null);

      dispatch(
        setLiveState({
          liveConfig: currentConfigId,
          liveCameraUrl: selectedCamera,
          liveModel: value
        })
      );

      const extractedModelData = getModelData(value, []);
      setModelData(extractedModelData);
      setSelectedModel(value);
    },
    [currentConfigId, selectedCamera, dispatch, getModelData, setModelData]
  );

  const handleWorkflowChange = useCallback(
    (value: string) => {
      setSelectedWorkflowId(value);
      dispatch(setWorkflowId(value));

      dispatch(
        setLiveState({
          liveConfig: currentConfigId,
          liveCameraUrl: selectedCamera,
          liveModel: selectedModel,
          liveWorkflow: value // Add workflow to liveState
        })
      );

      const selectedWorkflow: any = models?.data
        ?.flatMap(model => model.workflow_configurations || [])
        .find(workflow => workflow.id === value);
      if (value) {
        setSelectedWorkflowData(selectedWorkflow);

        const extractedModelData = getModelData(
          selectedModel || '',
          selectedWorkflow.data.workflow
        );
        setModelData(extractedModelData);
      }
    },
    [
      models?.data,
      modelData,
      currentConfigId,
      selectedCamera,
      selectedModel,
      dispatch
    ]
  );

  const handleCameraChange = useCallback(
    (value: string) => {
      console.log('selected value', value);
      setLoading(true);
      setResetting(false);
      endStream();

      const selectedCameraObj = cameraData?.data?.find(
        camera => camera.url === value
      );
      console.log('selected camera obj', selectedCameraObj);
      const camId = selectedCameraObj?.id || '';

      dispatch(setCameraId(camId));
      setLocalCameraId(camId);
      dispatch(
        setLiveState({
          liveConfig: currentConfigId,
          liveCameraUrl: value,
          liveModel: selectedModel
        })
      );
      if (socket) startStream(value, camId);
      setSelectedCamera(value);
      setStreamError(null);
    },
    [
      currentConfigId,
      selectedModel,
      cameraData,
      cameraId,
      endStream,
      startStream
    ]
  );

  const hasThirtyMinutesPassed = (lastTriggerTimestamp: number): boolean => {
    if (lastTriggerTimestamp === 0) return true;

    const thirtyMinutesInMs = 30 * 60 * 1000;
    const currentTime = Date.now();
    return currentTime - lastTriggerTimestamp >= thirtyMinutesInMs;
  };

  useEffect(() => {
    if (started) {
      window.history.pushState(
        { shouldPreventNavigation: true },
        '',
        window.location.pathname
      );

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (started) {
          e.preventDefault();
        }
      };

      const handlePopState = (event: PopStateEvent) => {
        if (started) {
          event.preventDefault();

          const confirmNavigation = window.confirm(
            'Going back will result in losing your data. Are you sure you want to proceed?'
          );

          if (!confirmNavigation) {
            window.history.pushState(
              { shouldPreventNavigation: true },
              '',
              window.location.pathname
            );
          } else {
            window.history.back();
          }
        }
      };

      const blockNavigation = (e: Event) => {
        if (started) {
          e.preventDefault();
          return false;
        }
      };

      window.onbeforeunload = handleBeforeUnload;
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', blockNavigation);

      return () => {
        window.onbeforeunload = null;
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', blockNavigation);
      };
    } else {
      window.onbeforeunload = null;
    }
  }, [started]);

  useEffect(() => {
    const allowRestart = hasThirtyMinutesPassed(lastTriggerTimestamp);
    if (!allowRestart && socket?.OPEN && connectionStatus === 'open') {
      restartInference(false);
    }
  }, [socket, connectionStatus]);

  // Effects
  useEffect(() => {
    if (cameraData !== undefined && socket?.OPEN && models) {
      setTimeout(() => {
        if (cameraLiveState.liveConfig === currentConfigId) {
          setSelectedCamera(cameraLiveState.liveCameraUrl);
          setSelectedModel(cameraLiveState.liveModel);

          let workflowToUse = [];
          let workflowDataToSet = null;

          if (cameraLiveState.liveWorkflow) {
            setSelectedWorkflowId(cameraLiveState.liveWorkflow);

            const foundWorkflow: any = models?.data
              ?.flatMap(model => model.workflow_configurations || [])
              .find(workflow => workflow.id === cameraLiveState.liveWorkflow);

            if (foundWorkflow) {
              setSelectedWorkflowData(foundWorkflow);
              workflowDataToSet = foundWorkflow;
              workflowToUse = foundWorkflow?.data?.workflow || [];
            }
          }

          setLoading(true);
          setResetting(false);
          endStream();

          const selectedCameraObj = cameraData?.data.find(
            camera => camera.url === cameraLiveState.liveCameraUrl
          );
          const camId = selectedCameraObj?.id || '';
          setLocalCameraId(camId);
          startStream(cameraLiveState.liveCameraUrl, camId);

          const extractedModelData = getModelData(
            cameraLiveState.liveModel,
            workflowToUse
          );
          setModelData(extractedModelData);
          setStreamError(null);
        }
      }, 2000);
    }

    return () => {
      if (started) {
        endInference();
      }
      if (image) {
        console.log('It is inside this function');
        endStream();
      }
    };
  }, [socket, models]);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollBy({ top: 6000, behavior: 'smooth' });
    }
  }, [activityData]);

  useEffect(() => {
    if (modelLoaded) {
      Swal.close();
    }
  }, [modelLoaded]);

  useEffect(() => {
    if (
      started &&
      selectedWorkflowId !== previousWorkflow &&
      previousWorkflow !== null &&
      selectedWorkflowId !== null &&
      selectedModel !== null &&
      selectedCamera !== null &&
      image
    ) {
      dispatch(setPreviousModel(selectedModel));
    }
  }, [
    started,
    selectedWorkflowId,
    previousWorkflow,
    selectedModel,
    selectedCamera,
    image,
    dispatch
  ]);

  // Nodes and Edges for Dependency Graph
  const nodes = useMemo(() => {
    if (!selectedModel || !selectedWorkflowData) return [];

    if (getConfigurationData?.data?.config_type === 'pick_and_place') {
      const workflowConfig =
        selectedWorkflowData?.data?.workflow[0]?.workflow_config;

      if (!workflowConfig || !Array.isArray(workflowConfig)) return [];

      return workflowConfig.map((item: any) => ({
        id: item.id,
        data: {
          label: item.label,
          mandatory: item.mandatory
        },
        position: {
          x: Math.random() * 500,
          y: Math.random() * 400
        }
      }));
    } else {
      if (
        !workflowData?.data ||
        !Array.isArray(workflowData.data) ||
        !selectedWorkflowId
      ) {
        return [];
      }

      const selectedWorkflow = workflowData.data.find(
        (workflow: any) => workflow.id === selectedWorkflowId
      );

      if (!selectedWorkflow || !selectedWorkflow.data?.workflow) {
        return [];
      }

      return selectedWorkflow.data.workflow.map((item: any, index: number) => ({
        id: item.id,
        data: {
          label:
            allActivities?.data?.activity_details?.find(
              (activity: any) => activity.id === item.activity_id
            )?.activity || '',
          mandatory: item.mandatory || false
        },
        position: {
          x: 200 * (index % 3),
          y: Math.floor(index / 3) * 100
        }
      }));
    }
  }, [selectedModel, selectedWorkflowData, getConfigurationData, workflowData]);

  const edges = useMemo(() => {
    if (!selectedModel || !selectedWorkflowData) return [];

    if (getConfigurationData?.data?.config_type === 'pick_and_place') {
      const workflowConfig =
        selectedWorkflowData?.data?.workflow[0]?.workflow_config;

      if (!workflowConfig) return [];

      return workflowConfig
        .filter(
          (item: any) => item.dependency && Array.isArray(item.dependency)
        )
        .flatMap((item: any) =>
          item.dependency.map((dep: any) => ({
            id: `e-${dep}-${item.id}`,
            source: dep,
            target: item.id,
            type: 'smoothstep',
            animated: true
          }))
        );
    } else {
      if (
        !workflowData?.data ||
        !Array.isArray(workflowData.data) ||
        !selectedWorkflowId
      ) {
        return [];
      }

      const selectedWorkflow = workflowData.data.find(
        (workflow: any) => workflow.id === selectedWorkflowId
      );

      // If no workflow found or no workflow data, return empty array
      if (!selectedWorkflow || !selectedWorkflow.data?.workflow) {
        return [];
      }

      // Now we can safely access the workflow array
      return selectedWorkflow.data.workflow
        .filter(
          (item: any) => item.dependency && Array.isArray(item.dependency)
        )
        .flatMap((item: any) =>
          item.dependency.map((dep: any) => ({
            id: `e-${dep}-${item.id}`,
            source: dep,
            target: item.id,
            type: 'smoothstep',
            animated: true
          }))
        );
    }
  }, [
    selectedModel,
    selectedWorkflowData,
    getConfigurationData,
    workflowData,
    selectedWorkflowId
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (image || started) {
        const confirmationMessage =
          'Live Stream is in progress! Are you sure you want to move out?';
        event.returnValue = confirmationMessage; // Standard for most browsers
        return confirmationMessage;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setImage('');
    }, 1); // Execute repeatedly with a 1 ms delay

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 1000); // Stop after 10 ms

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [cameraId]);

  return (
    <div className="w-full p-4 flex flex-col gap-4">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.1vw] font-[Proxima-Nova-Bold]">
          Live Inference
        </h2>
        <div className="flex items-center flex-end gap-2">
          {configs && (
            <SearchSelect
              label="Select Configuration"
              selectValue={currentConfigId || ''}
              data={[
                ...(configs?.data.map((config: any) => ({
                  label: config.name,
                  value: config.id
                })) || [])
              ]}
              disabled={true}
              placeholder="Configuration"
              width="12vw"
            />
          )}
          {!modelsLoading && (
            <SearchSelect
              label="Select Camera"
              selectValue={selectedCamera || ''}
              data={[
                ...(cameraData?.data.map((camera: any) => ({
                  label: camera.name,
                  value: camera.url
                })) || [])
              ]}
              placeholder="Select Camera"
              width="12vw"
              disabled={started}
              onChange={handleCameraChange}
            />
          )}
        </div>
      </div>
      <div className="flex flex-row items-start gap-4 justify-between">
        <div className="flex flex-col items-center justify-center gap-2 w-full flex-1 h-[65vh] mt-10">
          {!streamError ? (
            <div
              style={{
                position: 'relative',
                width: '80%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <StreamView
                image={image}
                loading={loading}
                selectedCamera={selectedCamera || ''}
                started={started}
              />
              <div
                style={{
                  position: 'absolute',
                  height: '200px',
                  width: '300px',
                  top: 10,
                  right: 0,
                  zIndex: 4
                }}
              >
                {activityData.length > 0 && (
                  <div className="notify-container">
                    <Repeat2
                      style={{
                        height: '30px',
                        width: '30px',
                        color: 'white'
                      }}
                    />
                    <div className="notify-main-data">
                      <p style={{ margin: 0, padding: 0 }}>
                        Cycle:{' '}
                        <span>
                          {activityData[activityData.length - 1]?.ncycles || 0}
                        </span>
                        <IconButton
                          disableRipple
                          style={{
                            color:
                              activityData[activityData.length - 1]
                                ?.sequence === 1
                                ? 'green'
                                : 'red',
                            alignContent: 'center'
                          }}
                        >
                          {activityData[activityData.length - 1]?.sequence ==
                          1 ? (
                            <CheckCircleIcon />
                          ) : (
                            <CancelRoundedIcon />
                          )}
                        </IconButton>
                      </p>
                    </div>
                  </div>
                )}
                <div className="notify-main-block" ref={divRef}>
                  {activityData?.map((data: any, index: number) => (
                    <NotificationItem
                      key={index}
                      data={data}
                      index={index}
                      activityDataLength={activityData.length}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                height: '600px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
              }}
            >
              <img
                src={StreamErrorImage}
                alt="error_stream"
                style={{ width: '350px' }}
              />
              <p style={{ fontSize: '16px' }}>{streamError}</p>
            </div>
          )}
          {!streamError && started && !modelLoaded ? (
            <button
              style={{
                background: 'red',
                padding: '8px 20px',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                opacity: 0.6,
                cursor: 'not-allowed',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '4px'
              }}
            >
              <Stop /> End Inference
            </button>
          ) : !streamError && started && modelLoaded ? (
            <button
              style={{
                background: 'red',
                padding: '8px 20px',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '4px',
                cursor: 'pointer'
              }}
              onClick={() => {
                endInference();
                // Note: We don't clear previousModel here as we want to remember it
              }}
            >
              <Stop /> End Inference
            </button>
          ) : (
            !streamError && (
              <button
                onClick={async () => {
                  if (
                    selectedModel === null ||
                    selectedCamera === null ||
                    !image ||
                    selectedWorkflowId === null
                  ) {
                    return;
                  }
                  dispatch(setInferenceTriggered(true));

                  dispatch(setPreviousModel(selectedModel));
                  dispatch(setPreviousWorkflow(selectedWorkflowData));
                  dispatch(
                    setLiveState({
                      liveConfig: currentConfigId,
                      liveCameraUrl: selectedCamera,
                      liveModel: selectedModel,
                      liveWorkflow: selectedWorkflowId
                    })
                  );
                  if (selectedCamera) {
                    if (
                      previousModel === 'startfresh' ||
                      previousModel === ''
                    ) {
                      Swal.fire({
                        title: 'Model Loading...',
                        toast: true,
                        allowOutsideClick: false,
                        position: 'bottom-right',
                        icon: 'info',
                        showConfirmButton: false,
                        timer: 10000
                      });
                      startLive(selectedCamera, cameraId || '');
                    } else if (
                      previousModel === selectedModel &&
                      previousWorkflow === selectedWorkflowId
                    ) {
                      console.log('RESUMING: Same model and workflow');
                      resumeInference();
                    } else {
                      restartInference(true);
                      setTimeout(() => {
                        startLive(selectedCamera, cameraId || '');
                      }, 2000);
                      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for restart to complete
                      Swal.fire({
                        title: 'Model Loading...',
                        toast: true,
                        allowOutsideClick: false,
                        position: 'bottom-right',
                        icon: 'info',
                        showConfirmButton: false,
                        timer: 10000
                      });
                    }
                  }
                }}
                style={{
                  background: '#102633',
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '4px',
                  opacity:
                    selectedModel === null ||
                    selectedCamera === null ||
                    !image ||
                    selectedWorkflowId === null
                      ? 0.8
                      : 1,
                  cursor:
                    selectedModel === null ||
                    selectedCamera === null ||
                    !image ||
                    selectedWorkflowId === null
                      ? 'not-allowed'
                      : 'pointer'
                }}
              >
                <NotStartedIcon /> Start Inference
              </button>
            )
          )}
          {!streamError &&
            (selectedModel === null ||
              selectedCamera === null ||
              selectedWorkflowId === null) && (
              <p>Please select Camera, Model and Workflow to Start Inference</p>
            )}
          {streamError && (
            <Button
              startIcon={<NotStartedIcon />}
              disabled={isReseting}
              loading={isReseting}
              onClick={() => {
                setResetting(true);
                sendMessage(
                  JSON.stringify({
                    event: 'kill_all'
                  })
                );
              }}
              variant="outlined"
            >
              Camera System Reset
            </Button>
          )}
        </div>
        <div className="flex flex-col flex-[0.4] items-start justify-start p-4 border-[1px] border-whitesmoke rounded-sm h-[70vh] gap-4">
          {/* <h3>Model & Workflow</h3> */}
          <div className="flex flex-row items-start justify-end gap-2 w-full">
            {!modelsLoading && (
              <SearchSelect
                label="Select Model"
                selectValue={selectedModel || ''}
                data={modelOptions}
                placeholder="Select Model"
                width="18vw"
                disabled={started}
                onChange={handleModelChange}
              />
            )}
            {!modelsLoading && (
              <SearchSelect
                label="Select Workflow"
                selectValue={selectedWorkflowId || ''}
                data={workflowOptions}
                placeholder="Select Workflow"
                width="18vw"
                disabled={started}
                onChange={handleWorkflowChange}
              />
            )}
          </div>
          {nodes && edges && (
            <DependancyGraph nodes={nodes} edges={edges} height={'60vh'} />
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveInference;
